/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {NumberString} from '@dvrd/dvr-controls';

import {
    CustomVATValue,
    DefaultVATValue,
    KORVATValue,
    MarginVATValue,
    NotSetVATValue,
    ReversedVATValue,
    SplitVATValue,
    VATPercentage,
    VATType,
    VATValue
} from './interfaces';

// Local NaN-safe sum. Inlined (rather than importing from ./utils) to keep
// vatUtil dependency-free of DOM/cookies/models — otherwise the chain
// utils → adminUtil → userModel → accountModel → bolProductModel → vatUtil
// becomes a runtime cycle that breaks under Vite/ESM (initialization order).
const sum = (...entries: Array<number>): number => entries.reduce((acc, val) => acc + (isNaN(val) ? 0 : val), 0);

export function translateVatValue(
    vatValue: VATValue | null,
    defaultValue?: VATValue | null,
    options?: {notSetAsText?: boolean; fullLabels?: boolean}
): string {
    const {fullLabels, notSetAsText} = options ?? {};
    if (!vatValue) {
        if (defaultValue) return notSetAsText ? 'Standaard' : translateVatValue(defaultValue);
        return '';
    }
    const {vat_type} = vatValue;
    if (vat_type === VATType.MARGIN) return 'Marge';
    else if (vat_type === VATType.SPLIT) return fullLabels ? 'Samengesteld' : 'Samengest.';
    else if (vat_type === VATType.REVERSED) return 'Verlegd';
    else if (vat_type === VATType.KOR) return 'KOR';
    else if (vat_type === VATType.NOT_SET && !!defaultValue) {
        if (notSetAsText) return 'Standaard';
        return translateVatValue(defaultValue);
    } else if (vat_type === VATType.CUSTOM) return 'Aangepast...';
    else return `${vatValue.value}%`;
}

export function VATValuesMatch(value1: VATValue | null, value2: VATValue | null): boolean {
    if (value1 === null || value2 === null) return false;
    if (!VATTypesMatch(value1, value2)) return false;
    if (value1.value === null && value2.value !== null) return false;
    if (value2.value === null && value1.value !== null) return false;
    if (value1.vat_type === VATType.SPLIT) {
        const v1Entries = Object.entries((value1 as SplitVATValue).value);
        const v2Value = (value2 as SplitVATValue).value;
        if (v1Entries.length !== Object.keys(v2Value).length) return false;
        for (const [percentage, value] of v1Entries) {
            if (v2Value[percentage] !== value) return false;
        }
        return true;
    }
    return value1.value === value2.value;
}

export function VATTypesMatch(value1: VATValue | null, value2: VATValue | null): boolean {
    if (!value1 || !value2) return false;
    return value1.vat_type === value2.vat_type;
}

export function calculateInclVAT(exclVAT: number, vatValue: VATValue): NumberString {
    const {value, vat_type} = vatValue;
    if (vat_type === VATType.DEFAULT) return new NumberString((exclVAT / 100) * (100 + value));
    else if ([VATType.MARGIN, VATType.KOR, VATType.REVERSED].includes(vat_type)) return new NumberString(exclVAT);
    else return new NumberString(exclVAT + sum(...Object.values(value as Record<string, number>)));
}

export function calculateExclVAT(inclVAT: number | NumberString, vatValue: VATValue): NumberString {
    const {value, vat_type} = vatValue;
    if (inclVAT instanceof NumberString) inclVAT = inclVAT.numberValue;
    if (vat_type === VATType.DEFAULT) return new NumberString((inclVAT / (100 + value)) * 100);
    else if ([VATType.MARGIN, VATType.KOR, VATType.REVERSED].includes(vat_type)) return new NumberString(inclVAT);
    else return new NumberString(inclVAT - sum(...Object.values(value as Record<string, number>)));
}

export function calculateVATAmount(
    vatValue: VATValue,
    inclVAT?: number | NumberString,
    exclVAT?: number | NumberString
): NumberString {
    if (inclVAT !== undefined) return calculateVATFromIncl(vatValue, inclVAT);
    else if (exclVAT !== undefined) return calculateVATFromExcl(vatValue, exclVAT);
    return new NumberString(0);
}

function calculateVATFromIncl(vatValue: VATValue, inclVAT: number | NumberString): NumberString {
    if (inclVAT instanceof NumberString) inclVAT = inclVAT.numberValue;
    const {vat_type, value} = vatValue;
    if (vat_type === VATType.DEFAULT) return new NumberString((inclVAT / (100 + value)) * value);
    else if ([VATType.MARGIN, VATType.KOR, VATType.REVERSED].includes(vat_type)) return new NumberString(0);
    else return new NumberString(sum(...Object.values(value as Record<string, number>)));
}

function calculateVATFromExcl(vatValue: VATValue, exclVAT: number | NumberString): NumberString {
    if (exclVAT instanceof NumberString) exclVAT = exclVAT.numberValue;
    const {vat_type, value} = vatValue;
    if (vat_type === VATType.DEFAULT) return new NumberString((exclVAT * value) / 100);
    else if ([VATType.MARGIN, VATType.KOR, VATType.REVERSED].includes(vat_type)) return new NumberString(0);
    else return new NumberString(sum(...Object.values(value as Record<string, number>)));
}

export class CountryVATValues<DataType extends VATValue = VATValue> {
    countryData: Map<string, DataType>;
    private readonly defaultValue: DataType | null = null;

    constructor(data: Record<string, DataType> | Map<string, DataType> = {}, defaultValue: DataType | null = null) {
        if (data instanceof Map) this.countryData = data;
        else this.countryData = new Map(Object.entries(data));
        this.defaultValue = defaultValue;
    }

    getCountryValue = (countryCode: string, apply_country_default: boolean = false): DataType | null => {
        if (this.countryData.has(countryCode)) return this.countryData.get(countryCode)!;
        if (apply_country_default) {
            let value = VATPercentage.HIGH;
            if (countryCode === 'DE') value = VATPercentage.DE_HIGH;
            return DefaultVAT(value) as DataType;
        }
        return this.defaultValue;
    };

    setCountryValue = (countryCode: string, value: DataType) => {
        this.countryData.set(countryCode, value);
        return this;
    };

    deleteCountryValue = (countryCode: string) => {
        this.countryData.delete(countryCode);
        return this;
    };

    getCountryType = (countryCode: string): VATType | null => {
        const value = this.getCountryValue(countryCode);
        if (value) return value.vat_type;
        return null;
    };

    hasCountryCode = (countryCode: string): boolean => {
        if (this.countryData.has(countryCode)) return true;
        return this.defaultValue !== null;
    };

    dump = (backendSafe: boolean = false): Record<string, DataType> => {
        let entries: Record<string, DataType> = {};
        if (backendSafe) {
            // Replace CUSTOM values and remove NOT_SET values
            for (const [countryCode, value] of this.entries()) {
                if (value.vat_type === VATType.NOT_SET) continue;
                if (value.vat_type === VATType.CUSTOM) value.vat_type = VATType.DEFAULT;
                entries[countryCode] = value as DataType;
            }
        } else entries = Object.fromEntries(this.countryData.entries());
        return entries;
    };

    clone = (): CountryVATValues<DataType> => {
        return new CountryVATValues(new Map(this.countryData), this.defaultValue);
    };

    keys = (): MapIterator<string> => {
        return this.countryData.keys();
    };

    entries = (): MapIterator<[string, VATValue]> => {
        return this.countryData.entries();
    };

    get countryCodes(): Array<string> {
        return Array.from(this.countryData.keys());
    }

    get sortedCountryCodes(): Array<string> {
        return this.countryCodes.toSorted((codeA: string, codeB: string) => {
            if (codeA === 'NL') return -3;
            else if (codeB === 'NL') return 3;
            else if (codeA === 'BE') return -2;
            else if (codeB === 'BE') return 2;
            else if (codeA === 'DE') return -1;
            else if (codeB === 'DE') return 1;
            return 0;
        });
    }

    get length(): number {
        return this.countryCodes.length;
    }
}

export function DefaultVAT(value: number): DefaultVATValue {
    return {vat_type: VATType.DEFAULT, value};
}

export function ReversedVAT(): ReversedVATValue {
    return {vat_type: VATType.REVERSED, value: null};
}

export function MarginVAT(): MarginVATValue {
    return {vat_type: VATType.MARGIN, value: null};
}

export function KORVAT(): KORVATValue {
    return {vat_type: VATType.KOR, value: null};
}

export function SplitVAT(values: Record<string, number>): SplitVATValue {
    return {vat_type: VATType.SPLIT, value: values};
}

export function CustomVAT(value: number): CustomVATValue {
    return {vat_type: VATType.CUSTOM, value};
}

export function NotSetVAT(): NotSetVATValue {
    return {vat_type: VATType.NOT_SET, value: null};
}
